import React, { useRef } from 'react';
import { Printer, Download, X, FileText, Calendar, User, Car, CreditCard, MapPin } from 'lucide-react';

const SalesInvoice = ({ sale, onClose }) => {
  const invoiceRef = useRef();

  const handlePrint = () => {
    const printContent = invoiceRef.current;
    const printWindow = window.open('', '', 'height=900,width=900');
    
    // Prepare services HTML (safe parsing & formatted list)
    const servicesData = sale.services ? (() => {
      try { return JSON.parse(sale.services); } catch (e) { return null; }
    })() : null;

    const formatServiceItem = (item) => {
      const name = item.serviceType?.name_en || item.name_en || item.name || 'Service';
      const vehicle = item.vehicleType?.name_en || item.vehicleType?.name ? ` - ${item.vehicleType?.name_en || item.vehicleType?.name}` : '';
      const cost = (item.cost != null) ? `QAR ${Number(item.cost).toFixed(2)}` : '';
      return `<div class="service-item"><div class="service-name">${name}${vehicle}</div><div class="service-cost">${cost}</div></div>`;
    };

    let servicesHtml = '';
    if (servicesData) {
      if (Array.isArray(servicesData.washCosts?.selected) && servicesData.washCosts.selected.length) {
      servicesHtml += `<div class="services-section"><div class="services-section-title">Wash Services</div>${servicesData.washCosts.selected.map(formatServiceItem).join('')}</div>`;
      }
      if (Array.isArray(servicesData.services?.selected) && servicesData.services.selected.length) {
      servicesHtml += `<div class="services-section"><div class="services-section-title">Additional Services</div>${servicesData.services.selected.map(formatServiceItem).join('')}</div>`;
      }
      if (Array.isArray(servicesData.products?.selected) && servicesData.products.selected.length) {
      servicesHtml += `<div class="services-section"><div class="services-section-title">Products</div>${servicesData.products.selected.map(formatServiceItem).join('')}</div>`;
      }
      if (Array.isArray(servicesData.addons?.selected) && servicesData.addons.selected.length) {
      servicesHtml += `<div class="services-section"><div class="services-section-title">Add-ons</div>${servicesData.addons.selected.map(formatServiceItem).join('')}</div>`;
      }
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
      <title>Invoice - ${sale.sales_id}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; background: white; color: #111827; }
        .invoice-content { max-width: 850px; margin: 0 auto; }
        /* ... existing styles (kept concise here) ... */

        /* Services specific */
        .services-section { margin-top: 10px; padding: 10px 12px; border-left: 4px solid #93c5fd; background: #f8fafc; border-radius: 6px; margin-bottom: 8px; }
        .services-section-title { font-weight: 700; color: #1f2937; font-size: 12px; margin-bottom: 8px; }
        .service-item { display: flex; justify-content: space-between; gap: 12px; align-items: center; font-size: 12px; color: #374151; padding: 4px 0; border-bottom: 1px dashed rgba(0,0,0,0.04); }
        .service-item:last-child { border-bottom: none; }
        .service-name { font-weight: 600; }
        .service-cost { font-weight: 700; color: #065f46; }
        /* table and totals styling (kept from original) */
        table { width: 100%; border-collapse: collapse; margin: 14px 0; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 6px rgba(0,0,0,0.05); }
        thead tr { background: linear-gradient(135deg, #374151 0%, #1f2937 100%); }
        th { color: white; padding: 12px; text-align: left; font-weight: 600; font-size: 11px; }
        td { padding: 12px; border-bottom: 1px solid #e5e7eb; background: white; font-size: 11px; vertical-align: top; }
        td.price-cell, td.discount-cell, td.total-cell { text-align: right; white-space: nowrap; }
        .description-main { font-weight: bold; font-size: 13px; color: #1f2937; margin-bottom: 6px; }
        .description-sub { font-size: 11px; color: #6b7280; margin-bottom: 8px; }
        @media print { body { padding: 8px; } @page { size: A4; margin: 8mm; } }
      </style>
      </head>
      <body>
      <div class="invoice-content">
        <!-- Header -->
        <div class="header-section">
        <div class="header-left">
          <!-- Inline SVG/logo -->
          <svg viewBox="0 0 374 72" fill="none" xmlns="http://www.w3.org/2000/svg" style="height:40px;margin-bottom:6px;">
            <path
              d="M127.564 70.3301C123.019 70.3301 119.087 69.2796 115.769 67.1975C112.451 65.1154 109.919 62.3075 108.155 58.7738C106.39 55.24 105.508 51.3242 105.508 47.0264C105.508 42.7286 106.39 38.8128 108.155 35.279C109.919 31.7453 112.47 28.9564 115.769 26.8553C119.087 24.7732 123.019 23.7227 127.564 23.7227C131.017 23.7227 134.124 24.3339 136.885 25.5373C139.647 26.7598 141.987 28.4216 143.924 30.5228C145.861 32.6239 147.3 35.088 148.278 37.8768H142.831C141.68 35.088 139.858 32.8531 137.384 31.2104C134.891 29.5677 131.765 28.7463 128.005 28.7463C124.323 28.7463 121.177 29.5677 118.588 31.1913C115.999 32.8149 114.024 35.0116 112.7 37.7622C111.358 40.5128 110.705 43.6072 110.705 47.0455C110.705 50.4264 111.377 53.5209 112.7 56.2906C114.043 59.0603 115.999 61.257 118.588 62.8806C121.177 64.5042 124.323 65.3256 128.005 65.3256C131.784 65.3256 134.91 64.5042 137.384 62.8615C139.877 61.2188 141.68 58.9839 142.831 56.1951H148.278C147.319 58.9457 145.861 61.3907 143.924 63.5109C141.987 65.6312 139.647 67.3121 136.885 68.5346C134.124 69.738 131.017 70.3301 127.564 70.3301Z"
              fill="#67BADB"
            />
            <path
              d="M157.711 69.7965V24.2969H162.889V64.8301H187.804V69.7774H157.711V69.7965Z"
              fill="#67BADB"
            />
            <path
              d="M196.211 69.7965V24.2969H229.813V29.2441H201.389V44.5062H226.783V49.5299H201.389V64.8492H229.813V69.7965H196.211Z"
              fill="#67BADB"
            />
            <path
              d="M234.508 69.7969L253.898 24.3164H260.381L279.771 69.7969H274.037L268.456 56.5978H245.824L240.242 69.7969H234.508ZM247.818 51.8607H266.442L257.12 29.6648L247.818 51.8607Z"
              fill="#67BADB"
            />
            <path
              d="M287.273 69.7965V24.2969H293.277L321.49 62.5761V24.2969H326.668V69.7774H320.665L292.452 31.3644V69.7774H287.273V69.7965Z"
              fill="#67BADB"
            />
            <path
              d="M355.915 70.3306C351.408 70.3306 347.745 69.7193 344.945 68.5159C342.145 67.2934 340.035 65.6125 338.635 63.4732C337.235 61.3147 336.41 58.8888 336.18 56.1955H341.569C341.703 58.3922 342.451 60.1686 343.775 61.5248C345.117 62.881 346.863 63.8743 349.049 64.5046C351.236 65.1541 353.633 65.4597 356.261 65.4597C360.116 65.4597 363.184 64.7911 365.428 63.4349C367.672 62.0787 368.804 60.0731 368.804 57.418C368.804 55.8135 368.248 54.5146 367.116 53.5404C365.985 52.5471 364.489 51.7449 362.628 51.0954C360.768 50.446 358.735 49.8729 356.529 49.3381C354.323 48.8033 352.099 48.2493 349.874 47.6572C347.649 47.065 345.597 46.301 343.736 45.3841C341.876 44.4672 340.38 43.2829 339.248 41.8503C338.117 40.3986 337.561 38.5649 337.561 36.3109C337.561 32.6052 338.999 29.5872 341.876 27.2377C344.753 24.9074 349.03 23.7422 354.745 23.7422C358.792 23.7422 362.13 24.3534 364.757 25.5568C367.385 26.7793 369.341 28.3647 370.645 30.3322C371.949 32.2996 372.678 34.439 372.813 36.7312H367.5C367.231 34.439 366.081 32.5097 364.047 30.9243C362.014 29.3389 358.927 28.5557 354.726 28.5557C352.156 28.5557 350.066 28.8041 348.455 29.3198C346.843 29.8164 345.616 30.485 344.772 31.2872C343.928 32.0895 343.353 32.93 343.046 33.7895C342.739 34.6682 342.605 35.4896 342.605 36.2727C342.605 37.7817 343.161 39.0042 344.293 39.9402C345.424 40.8762 346.92 41.6593 348.781 42.2706C350.641 42.8818 352.693 43.4549 354.956 43.9515C357.2 44.4481 359.444 45.0212 361.688 45.6324C363.913 46.2437 365.965 47.0268 367.826 47.9628C369.686 48.8988 371.182 50.1022 372.314 51.573C373.445 53.0438 374.002 54.9157 374.002 57.2079C374.002 61.4293 372.391 64.6574 369.168 66.9305C365.946 69.2036 361.535 70.3306 355.915 70.3306Z"
              fill="#67BADB"
            />
            <path
              d="M80.0558 14.5171L77.409 4.39333C76.7377 1.81464 74.3786 0 71.6935 0H28.8082C26.123 0 23.7831 1.81464 23.0927 4.39333L20.4267 14.5744C20.1774 15.4913 20.7336 16.4463 21.6734 16.6755C22.594 16.9239 23.553 16.3699 23.7831 15.434L26.4491 5.25289V5.23379C26.6984 4.31692 27.6958 3.83939 28.5972 4.12591C29.2877 4.35513 30.0357 4.60344 30.0357 4.60344C32.2797 5.25289 34.5237 5.74953 36.7677 6.15066C41.2557 6.91472 45.7437 7.27764 50.2317 7.27764C54.7197 7.27764 59.2077 6.91472 63.6957 6.13156C65.9397 5.73043 68.1837 5.23379 70.4277 4.58434C70.4277 4.58434 71.1948 4.33602 71.8661 4.10681C72.7676 3.82029 73.7649 4.29782 74.0142 5.21469C74.0142 5.23379 74.0142 5.23379 74.0142 5.25289L76.6802 15.434C76.8911 16.2171 77.5816 16.7329 78.3488 16.7329C78.5789 16.7329 78.8091 16.6946 79.0392 16.58C79.8831 16.2553 80.2667 15.3575 80.0558 14.5171Z"
              fill="#FF8C00"
            />
            <path
              d="M90.3356 23.3225L89.7603 21.4123C89.2616 19.7696 87.9766 18.4898 86.3271 17.9932L84.4092 17.4201C83.6995 17.21 83.6995 16.2167 84.4092 16.0066L86.3271 15.4336C87.9766 14.9369 89.2616 13.6571 89.7603 12.0144L90.3356 10.1043C90.5466 9.39753 91.544 9.39753 91.7549 10.1043L92.3303 12.0144C92.829 13.6571 94.114 14.9369 95.7634 15.4336L97.6814 16.0066C98.391 16.2167 98.391 17.21 97.6814 17.4201L95.7634 17.9932C94.114 18.4898 92.829 19.7696 92.3303 21.4123L91.7549 23.3225C91.544 24.0101 90.5466 24.0101 90.3356 23.3225Z"
              fill="#66B8D9"
            />
            <path
              d="M41.2369 43.9141H26.2769C25.318 43.9141 24.5508 44.6781 24.5508 45.6332C24.5508 46.5883 25.318 47.3523 26.2769 47.3523H41.2369C42.1959 47.3523 42.9631 46.5883 42.9631 45.6332C42.9631 44.6781 42.1767 43.9141 41.2369 43.9141Z"
              fill="#FF8C00"
            />
            <path
              d="M72.7681 20.2281C70.294 20.2281 64.3292 19.9034 57.9616 23.5517C53.3777 26.1686 49.1965 29.9125 46.7032 34.8598C46.0895 36.1014 45.5716 37.4003 45.1305 38.7183C44.7085 40.0554 44.4209 41.4307 44.1907 42.806C44.0948 43.4936 43.9989 44.2004 43.9605 44.9071L43.9222 45.442L43.903 45.5757V45.7285V45.9577L43.8455 46.6454C43.7688 47.5431 43.6345 48.4409 43.4235 49.3196C43.0016 51.0578 42.3878 52.7387 41.5056 54.2859C41.0644 55.05 40.6041 55.814 40.0671 56.5208C39.5301 57.2276 38.9547 57.8961 38.3218 58.5264C37.6889 59.1568 37.0368 59.7489 36.3271 60.2838C35.6175 60.8186 34.8695 61.2962 34.1023 61.7355C32.5679 62.6333 30.8801 63.2636 29.1348 63.6838C28.2717 63.9131 27.3703 64.0468 26.4689 64.1423L25.7784 64.1996L25.0304 64.2378C24.5701 64.276 24.1098 64.2569 23.6495 64.276C22.7289 64.2569 21.7891 64.1805 20.8684 64.0277C19.9478 63.8749 19.0272 63.6838 18.1066 63.4164C14.4625 62.3467 10.9718 60.2265 8.15245 57.1894C7.8264 56.8455 7.46199 56.3871 7.0784 55.8905C4.48916 52.4331 3.18496 48.1544 3.53019 43.8566C3.54937 43.6273 3.56855 43.3981 3.60691 43.1689C4.89193 31.9755 14.5584 23.6854 25.8168 23.6854H36.7299V27.4484C36.7299 29.1485 38.13 30.5429 39.837 30.5429C41.333 30.5429 42.5988 29.4732 42.8865 28.0597C42.9249 27.9069 42.9632 27.7349 42.9632 27.563V20.2281C42.9632 20.2281 42.9632 20.209 42.944 20.209H25.8168C11.7774 20.209 0.116242 31.3642 0.00116485 45.3465C-0.133092 59.4624 11.3554 70.9615 25.4907 70.9615H25.8168C32.5296 70.9615 38.8204 68.3446 43.5386 63.6074C48.2568 58.8703 50.8268 52.5668 50.7693 45.8813V45.5757C50.7693 33.4845 60.6467 23.6472 72.7873 23.6472C80.5358 23.6472 87.3637 27.6585 91.2955 33.7137C92.1586 35.1845 97.8549 45.6903 89.953 55.0309C89.6461 55.3556 89.32 55.6803 89.0132 56.0051C86.3472 58.6602 83.4128 60.6467 80.2673 62.0793C79.481 62.4422 78.6754 62.767 77.8507 63.0344C77.4479 63.1872 77.026 63.3209 76.604 63.4546C76.4506 63.5119 76.2972 63.5501 76.1245 63.5883C75.9711 63.6265 75.7985 63.6647 75.6451 63.722L75.319 63.7984C74.4559 64.0468 73.5737 64.1423 72.6722 64.3142L71.9818 64.4097C71.7708 64.4479 71.5406 64.4479 71.3297 64.4861C71.0995 64.5243 70.8694 64.5434 70.6392 64.5625C70.3899 64.5816 70.1405 64.6198 69.9104 64.6198C69.6802 64.6198 69.4309 64.6389 69.2008 64.658L68.4911 64.6771C68.2418 64.6962 68.0116 64.6771 67.7623 64.6771C67.513 64.6771 67.2636 64.6771 67.0335 64.6771C66.0361 64.658 65.0196 64.5816 63.9839 64.4479C62.2194 64.1996 59.1315 62.7861 58.2684 62.0984C57.5972 61.5636 56.6382 61.4681 55.9861 62.022C55.1422 62.7097 55.1422 63.9704 55.9285 64.658C60.5892 68.7648 66.5923 71.0188 72.8065 71.0188C86.8651 71.0188 98.296 59.6343 98.296 45.633C98.296 31.6316 86.8267 20.2281 72.7681 20.2281Z"
              fill="#FF8C00"
            />
          </svg>
          <p style="color:#4b5563;font-size:13px;">Premium Car Wash Services</p>
          <p style="color:#6b7280;font-size:11px;">Quality & Excellence in Every Wash</p>
        </div>
        <div class="header-right" style="text-align:right;">
          <div class="invoice-badge" style="background:#2563eb;color:#fff;padding:8px 12px;border-radius:6px;display:inline-block;font-weight:700;margin-bottom:8px;">INVOICE / فاتورة</div>
          <p dir="rtl" style="margin:2px 0;font-size:12px;font-weight:600;">Invoice # / رقم الفاتورة: ${sale.sales_id}</p>
          <p dir="rtl" style="margin:2px 0;font-size:11px;color:#374151;">Date / التاريخ: ${formatDate(sale.createdAt)}</p>
          <p dir="rtl" style="margin:2px 0;font-size:11px;color:#374151;">Time / الوقت: ${formatTime(sale.createdAt)}</p>
        </div>
        </div>

        <!-- Customer & Vehicle Info (kept same structure) -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;">
        <div style="padding:14px;border-radius:10px;background:linear-gradient(135deg,#eff6ff 0%,#dbeafe 100%);border:2px solid #93c5fd;">
          <div style="font-weight:700;font-size:13px;margin-bottom:12px;color:#1f2937;border-bottom:1px solid rgba(0,0,0,0.06);padding-bottom:8px;">Customer Information / معلومات العميل</div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Name / الاسم</div><div style="font-weight:600;color:#1f2937;">${sale.client.username}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Mobile / الجوال</div><div style="font-weight:600;color:#1f2937;">${sale.client.mobile}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Email / البريد</div><div style="font-weight:600;color:#1f2937;">${sale.client.email}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;"><div style="color:#4b5563;font-weight:600;">Client ID / رقم العميل</div><div style="font-weight:600;color:#1f2937;">${sale.client.client_id}</div></div>
        </div>

        <div style="padding:14px;border-radius:10px;background:linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%);border:2px solid #86efac;">
          <div style="font-weight:700;font-size:13px;margin-bottom:12px;color:#1f2937;border-bottom:1px solid rgba(0,0,0,0.06);padding-bottom:8px;">Vehicle Information / معلومات المركبة</div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Model / الطراز</div><div style="font-weight:600;color:#1f2937;">${sale.vehicle?.model || 'N/A'}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Plate / اللوحة</div><div style="font-weight:600;color:#1f2937;">${sale.vehicle?.plate_number || 'N/A'}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Color / اللون</div><div style="font-weight:600;color:#1f2937;"><span style="display:inline-block;width:18px;height:18px;border:2px solid #e5e7eb;background:${sale.vehicle?.color || '#000'};border-radius:4px;"></span></div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;"><div style="color:#4b5563;font-weight:600;">Year / السنة</div><div style="font-weight:600;color:#1f2937;">${sale.vehicle?.year_made || 'N/A'}</div></div>
        </div>
        </div>

        ${sale.clientSubscription ? `
        <div style="padding:14px;border-radius:10px;background:linear-gradient(135deg,#fff7ed 0%,#ffedd5 100%);border:2px solid #fed7aa;margin-bottom:10px;">
          <div style="font-weight:700;font-size:13px;margin-bottom:12px;color:#1f2937;border-bottom:1px solid rgba(0,0,0,0.06);padding-bottom:8px;">Subscription Details / تفاصيل الاشتراك</div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Plan Name / اسم الخطة</div><div style="font-weight:600;color:#1f2937;">${sale.clientSubscription.subscription_name}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Plan Cost / تكلفة الخطة</div><div style="font-weight:600;color:#1f2937;">QAR ${sale.clientSubscription.subscription_cost}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:8px;"><div style="color:#4b5563;font-weight:600;">Start / تاريخ البدء</div><div style="font-weight:600;color:#1f2937;">${formatDate(sale.clientSubscription.start_date)}</div></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;"><div style="color:#4b5563;font-weight:600;">End / تاريخ الانتهاء</div><div style="font-weight:600;color:#1f2937;">${formatDate(sale.clientSubscription.end_date)}</div></div>
        </div>
        ` : ''}

        <!-- Invoice Table -->
        <table>
        <thead>
          <tr>
          <th>Description / الوصف</th>
          <th style="text-align:right;">Price / السعر</th>
          <th style="text-align:right;">Discount / الخصم</th>
          <th style="text-align:right;">Total / الإجمالي</th>
          </tr>
        </thead>
        <tbody>
          <tr>
          <td>
            <div class="description-main">${sale.action}</div>
            <div class="description-sub">Payment Method / طريقة الدفع: ${sale.payment_type}</div>
            ${servicesHtml || ''}
            ${sale.coupon_id ? `<div style="color:#16a34a;font-weight:600;margin-top:8px;">Coupon Applied / كوبون مطبق: #${sale.coupon_id}</div>` : ''}
          </td>
          <td class="price-cell">QAR ${Number(sale.price).toFixed(2)}</td>
          <td class="discount-cell">${sale.discount > 0 ? `-QAR ${Number(sale.discount).toFixed(2)}` : 'QAR 0.00'}</td>
          <td class="total-cell">QAR ${Number(sale.total).toFixed(2)}</td>
          </tr>
        </tbody>
        </table>

        <!-- Totals -->
        <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <div style="width:320px;background:#f9fafb;padding:16px;border-radius:10px;border:1px solid #e5e7eb;">
          <div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #d1d5db;font-size:12px;">
          <span style="font-weight:600;color:#374151;">Subtotal / المجموع الفرعي</span><span style="font-weight:600;color:#111827;">QAR ${Number(sale.price).toFixed(2)}</span>
          </div>
          ${sale.discount > 0 ? `<div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #d1d5db;font-size:12px;"><span style="font-weight:600;color:#374151;">Discount / الخصم</span><span style="font-weight:700;color:#dc2626;">- QAR ${Number(sale.discount).toFixed(2)}</span></div>` : ''}
          <div style="display:flex;justify-content:space-between;padding-top:12px;border-top:2px solid #1f2937;margin-top:8px;">
          <span style="font-weight:800;font-size:14px;color:#1f2937;">Total Amount / المبلغ الإجمالي</span><span style="font-weight:800;font-size:16px;color:#2563eb;">QAR ${Number(sale.total).toFixed(2)}</span>
          </div>
        </div>
        </div>

        <!-- Payment Badge & Footer -->
        <div style="background:linear-gradient(135deg,#10b981 0%,#059669 100%);color:#fff;padding:14px;border-radius:10px;text-align:center;margin-top:16px;font-weight:700;">💳 PAYMENT CONFIRMED / الدفع مؤكد - ${sale.payment_type}</div>

        <div style="border-top:4px solid #2563eb;padding-top:16px;margin-top:20px;text-align:center;">
        <div style="font-weight:800;font-size:18px;color:#1f2937;">GO CLEANS</div>
        <div style="color:#374151;font-weight:600;margin-bottom:8px;">Premium Car Wash Services - Qatar</div>
        <div style="color:#4b5563;font-size:11px;">📍 Commercial Avenue, Umm Al Seneem, Doha, Qatar • 📞 +974 7020 5550 • 📧 info@gocleans.co • 🌐 www.gocleans.co</div>
        <div style="margin-top:12px;font-size:11px;color:#6b7280;font-style:italic;">Thank you for choosing GO CLEANS! / شكراً لاختياركم جو كلينز!</div>
        </div>
      </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleSavePDF = () => {
    handlePrint();
  };

  if (!sale) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full mx-4 mb-8">
        {/* Fixed Header Actions */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 border-b bg-white rounded-t-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-800">Sales Invoice</h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer size={18} />
              Print
            </button>
            <button
              onClick={handleSavePDF}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download size={18} />
              Save PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Scrollable Invoice Content */}
        <div ref={invoiceRef} className="invoice-content p-8">
          {/* Company Header */}
          <div className="header-section border-b-4 border-blue-600 pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                          <svg
            className="h-6"
            viewBox="0 0 374 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M127.564 70.3301C123.019 70.3301 119.087 69.2796 115.769 67.1975C112.451 65.1154 109.919 62.3075 108.155 58.7738C106.39 55.24 105.508 51.3242 105.508 47.0264C105.508 42.7286 106.39 38.8128 108.155 35.279C109.919 31.7453 112.47 28.9564 115.769 26.8553C119.087 24.7732 123.019 23.7227 127.564 23.7227C131.017 23.7227 134.124 24.3339 136.885 25.5373C139.647 26.7598 141.987 28.4216 143.924 30.5228C145.861 32.6239 147.3 35.088 148.278 37.8768H142.831C141.68 35.088 139.858 32.8531 137.384 31.2104C134.891 29.5677 131.765 28.7463 128.005 28.7463C124.323 28.7463 121.177 29.5677 118.588 31.1913C115.999 32.8149 114.024 35.0116 112.7 37.7622C111.358 40.5128 110.705 43.6072 110.705 47.0455C110.705 50.4264 111.377 53.5209 112.7 56.2906C114.043 59.0603 115.999 61.257 118.588 62.8806C121.177 64.5042 124.323 65.3256 128.005 65.3256C131.784 65.3256 134.91 64.5042 137.384 62.8615C139.877 61.2188 141.68 58.9839 142.831 56.1951H148.278C147.319 58.9457 145.861 61.3907 143.924 63.5109C141.987 65.6312 139.647 67.3121 136.885 68.5346C134.124 69.738 131.017 70.3301 127.564 70.3301Z"
              fill="#67BADB"
            />
            <path
              d="M157.711 69.7965V24.2969H162.889V64.8301H187.804V69.7774H157.711V69.7965Z"
              fill="#67BADB"
            />
            <path
              d="M196.211 69.7965V24.2969H229.813V29.2441H201.389V44.5062H226.783V49.5299H201.389V64.8492H229.813V69.7965H196.211Z"
              fill="#67BADB"
            />
            <path
              d="M234.508 69.7969L253.898 24.3164H260.381L279.771 69.7969H274.037L268.456 56.5978H245.824L240.242 69.7969H234.508ZM247.818 51.8607H266.442L257.12 29.6648L247.818 51.8607Z"
              fill="#67BADB"
            />
            <path
              d="M287.273 69.7965V24.2969H293.277L321.49 62.5761V24.2969H326.668V69.7774H320.665L292.452 31.3644V69.7774H287.273V69.7965Z"
              fill="#67BADB"
            />
            <path
              d="M355.915 70.3306C351.408 70.3306 347.745 69.7193 344.945 68.5159C342.145 67.2934 340.035 65.6125 338.635 63.4732C337.235 61.3147 336.41 58.8888 336.18 56.1955H341.569C341.703 58.3922 342.451 60.1686 343.775 61.5248C345.117 62.881 346.863 63.8743 349.049 64.5046C351.236 65.1541 353.633 65.4597 356.261 65.4597C360.116 65.4597 363.184 64.7911 365.428 63.4349C367.672 62.0787 368.804 60.0731 368.804 57.418C368.804 55.8135 368.248 54.5146 367.116 53.5404C365.985 52.5471 364.489 51.7449 362.628 51.0954C360.768 50.446 358.735 49.8729 356.529 49.3381C354.323 48.8033 352.099 48.2493 349.874 47.6572C347.649 47.065 345.597 46.301 343.736 45.3841C341.876 44.4672 340.38 43.2829 339.248 41.8503C338.117 40.3986 337.561 38.5649 337.561 36.3109C337.561 32.6052 338.999 29.5872 341.876 27.2377C344.753 24.9074 349.03 23.7422 354.745 23.7422C358.792 23.7422 362.13 24.3534 364.757 25.5568C367.385 26.7793 369.341 28.3647 370.645 30.3322C371.949 32.2996 372.678 34.439 372.813 36.7312H367.5C367.231 34.439 366.081 32.5097 364.047 30.9243C362.014 29.3389 358.927 28.5557 354.726 28.5557C352.156 28.5557 350.066 28.8041 348.455 29.3198C346.843 29.8164 345.616 30.485 344.772 31.2872C343.928 32.0895 343.353 32.93 343.046 33.7895C342.739 34.6682 342.605 35.4896 342.605 36.2727C342.605 37.7817 343.161 39.0042 344.293 39.9402C345.424 40.8762 346.92 41.6593 348.781 42.2706C350.641 42.8818 352.693 43.4549 354.956 43.9515C357.2 44.4481 359.444 45.0212 361.688 45.6324C363.913 46.2437 365.965 47.0268 367.826 47.9628C369.686 48.8988 371.182 50.1022 372.314 51.573C373.445 53.0438 374.002 54.9157 374.002 57.2079C374.002 61.4293 372.391 64.6574 369.168 66.9305C365.946 69.2036 361.535 70.3306 355.915 70.3306Z"
              fill="#67BADB"
            />
            <path
              d="M80.0558 14.5171L77.409 4.39333C76.7377 1.81464 74.3786 0 71.6935 0H28.8082C26.123 0 23.7831 1.81464 23.0927 4.39333L20.4267 14.5744C20.1774 15.4913 20.7336 16.4463 21.6734 16.6755C22.594 16.9239 23.553 16.3699 23.7831 15.434L26.4491 5.25289V5.23379C26.6984 4.31692 27.6958 3.83939 28.5972 4.12591C29.2877 4.35513 30.0357 4.60344 30.0357 4.60344C32.2797 5.25289 34.5237 5.74953 36.7677 6.15066C41.2557 6.91472 45.7437 7.27764 50.2317 7.27764C54.7197 7.27764 59.2077 6.91472 63.6957 6.13156C65.9397 5.73043 68.1837 5.23379 70.4277 4.58434C70.4277 4.58434 71.1948 4.33602 71.8661 4.10681C72.7676 3.82029 73.7649 4.29782 74.0142 5.21469C74.0142 5.23379 74.0142 5.23379 74.0142 5.25289L76.6802 15.434C76.8911 16.2171 77.5816 16.7329 78.3488 16.7329C78.5789 16.7329 78.8091 16.6946 79.0392 16.58C79.8831 16.2553 80.2667 15.3575 80.0558 14.5171Z"
              fill="#FF8C00"
            />
            <path
              d="M90.3356 23.3225L89.7603 21.4123C89.2616 19.7696 87.9766 18.4898 86.3271 17.9932L84.4092 17.4201C83.6995 17.21 83.6995 16.2167 84.4092 16.0066L86.3271 15.4336C87.9766 14.9369 89.2616 13.6571 89.7603 12.0144L90.3356 10.1043C90.5466 9.39753 91.544 9.39753 91.7549 10.1043L92.3303 12.0144C92.829 13.6571 94.114 14.9369 95.7634 15.4336L97.6814 16.0066C98.391 16.2167 98.391 17.21 97.6814 17.4201L95.7634 17.9932C94.114 18.4898 92.829 19.7696 92.3303 21.4123L91.7549 23.3225C91.544 24.0101 90.5466 24.0101 90.3356 23.3225Z"
              fill="#66B8D9"
            />
            <path
              d="M41.2369 43.9141H26.2769C25.318 43.9141 24.5508 44.6781 24.5508 45.6332C24.5508 46.5883 25.318 47.3523 26.2769 47.3523H41.2369C42.1959 47.3523 42.9631 46.5883 42.9631 45.6332C42.9631 44.6781 42.1767 43.9141 41.2369 43.9141Z"
              fill="#FF8C00"
            />
            <path
              d="M72.7681 20.2281C70.294 20.2281 64.3292 19.9034 57.9616 23.5517C53.3777 26.1686 49.1965 29.9125 46.7032 34.8598C46.0895 36.1014 45.5716 37.4003 45.1305 38.7183C44.7085 40.0554 44.4209 41.4307 44.1907 42.806C44.0948 43.4936 43.9989 44.2004 43.9605 44.9071L43.9222 45.442L43.903 45.5757V45.7285V45.9577L43.8455 46.6454C43.7688 47.5431 43.6345 48.4409 43.4235 49.3196C43.0016 51.0578 42.3878 52.7387 41.5056 54.2859C41.0644 55.05 40.6041 55.814 40.0671 56.5208C39.5301 57.2276 38.9547 57.8961 38.3218 58.5264C37.6889 59.1568 37.0368 59.7489 36.3271 60.2838C35.6175 60.8186 34.8695 61.2962 34.1023 61.7355C32.5679 62.6333 30.8801 63.2636 29.1348 63.6838C28.2717 63.9131 27.3703 64.0468 26.4689 64.1423L25.7784 64.1996L25.0304 64.2378C24.5701 64.276 24.1098 64.2569 23.6495 64.276C22.7289 64.2569 21.7891 64.1805 20.8684 64.0277C19.9478 63.8749 19.0272 63.6838 18.1066 63.4164C14.4625 62.3467 10.9718 60.2265 8.15245 57.1894C7.8264 56.8455 7.46199 56.3871 7.0784 55.8905C4.48916 52.4331 3.18496 48.1544 3.53019 43.8566C3.54937 43.6273 3.56855 43.3981 3.60691 43.1689C4.89193 31.9755 14.5584 23.6854 25.8168 23.6854H36.7299V27.4484C36.7299 29.1485 38.13 30.5429 39.837 30.5429C41.333 30.5429 42.5988 29.4732 42.8865 28.0597C42.9249 27.9069 42.9632 27.7349 42.9632 27.563V20.2281C42.9632 20.2281 42.9632 20.209 42.944 20.209H25.8168C11.7774 20.209 0.116242 31.3642 0.00116485 45.3465C-0.133092 59.4624 11.3554 70.9615 25.4907 70.9615H25.8168C32.5296 70.9615 38.8204 68.3446 43.5386 63.6074C48.2568 58.8703 50.8268 52.5668 50.7693 45.8813V45.5757C50.7693 33.4845 60.6467 23.6472 72.7873 23.6472C80.5358 23.6472 87.3637 27.6585 91.2955 33.7137C92.1586 35.1845 97.8549 45.6903 89.953 55.0309C89.6461 55.3556 89.32 55.6803 89.0132 56.0051C86.3472 58.6602 83.4128 60.6467 80.2673 62.0793C79.481 62.4422 78.6754 62.767 77.8507 63.0344C77.4479 63.1872 77.026 63.3209 76.604 63.4546C76.4506 63.5119 76.2972 63.5501 76.1245 63.5883C75.9711 63.6265 75.7985 63.6647 75.6451 63.722L75.319 63.7984C74.4559 64.0468 73.5737 64.1423 72.6722 64.3142L71.9818 64.4097C71.7708 64.4479 71.5406 64.4479 71.3297 64.4861C71.0995 64.5243 70.8694 64.5434 70.6392 64.5625C70.3899 64.5816 70.1405 64.6198 69.9104 64.6198C69.6802 64.6198 69.4309 64.6389 69.2008 64.658L68.4911 64.6771C68.2418 64.6962 68.0116 64.6771 67.7623 64.6771C67.513 64.6771 67.2636 64.6771 67.0335 64.6771C66.0361 64.658 65.0196 64.5816 63.9839 64.4479C62.2194 64.1996 59.1315 62.7861 58.2684 62.0984C57.5972 61.5636 56.6382 61.4681 55.9861 62.022C55.1422 62.7097 55.1422 63.9704 55.9285 64.658C60.5892 68.7648 66.5923 71.0188 72.8065 71.0188C86.8651 71.0188 98.296 59.6343 98.296 45.633C98.296 31.6316 86.8267 20.2281 72.7681 20.2281Z"
              fill="#FF8C00"
            />
          </svg>
                <p className="text-gray-600 text-lg">Premium Car Wash Services</p>
                <p className="text-gray-500 text-sm mt-1">Quality & Excellence in Every Wash</p>
              </div>
              <div className="text-right">
                <div className="bg-blue-600 text-white px-4 py-2 rounded-lg mb-2 inline-block">
                  <p className="text-2xl font-bold">INVOICE</p>
                </div>
                <div>
                  <p className="text-gray-700 font-semibold">Invoice #: {sale.sales_id}</p>
                  <p className="text-gray-600 text-sm">Date: {formatDate(sale.createdAt)}</p>
                  <p className="text-gray-600 text-sm">Time: {formatTime(sale.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Customer & Branch Information */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="info-box bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border border-blue-200">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-lg">
                <div className="bg-blue-600 p-2 rounded-lg">
                  <User size={20} className="text-white" />
                </div>
                Customer Information
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Name:</span>
                  <span className="text-gray-800 font-semibold">{sale.client.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Mobile:</span>
                  <span className="text-gray-800 font-semibold">{sale.client.mobile}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Email:</span>
                  <span className="text-gray-800 font-semibold text-sm">{sale.client.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Client ID:</span>
                  <span className="text-gray-800 font-mono text-sm">{sale.client.client_id}</span>
                </div>
              </div>
            </div>

            <div className="info-box bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border border-green-200">
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-lg">
              <div className="bg-purple-600 p-2 rounded-lg">
                <Car size={20} className="text-white" />
              </div>
              Vehicle Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Model:</span>
                <span className="text-gray-800 font-semibold">{sale.vehicle?.model || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Plate Number:</span>
                <span className="text-gray-800 font-semibold font-mono">{sale.vehicle?.plate_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Color:</span>
                <div className="flex items-center gap-2">
                  <span 
                    className="inline-block w-8 h-8 rounded border-2 border-gray-300 shadow-sm" 
                    style={{backgroundColor: sale.vehicle?.color || '#000'}}
                  ></span>
                  
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Year Made:</span>
                <span className="text-gray-800 font-semibold">{sale.vehicle?.year_made || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Registration:</span>
                <span className="text-gray-800 font-semibold">{sale.vehicle?.registration || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Vehicle ID:</span>
                <span className="text-gray-800 font-mono text-sm">{sale.vehicle_id}</span>
              </div>
            </div>
          </div>
</div>
          {/* Vehicle Information */}
 

          {/* Subscription Details (if applicable) */}
          {sale.clientSubscription && (
            <div className="info-box bg-gradient-to-br from-orange-50 to-orange-100 p-5 rounded-xl mb-6 border border-orange-200">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-lg">
                <div className="bg-orange-600 p-2 rounded-lg">
                  <Calendar size={20} className="text-white" />
                </div>
                Subscription Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Plan Name:</span>
                  <span className="text-gray-800 font-semibold">{sale.clientSubscription.subscription_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Plan Cost:</span>
                  <span className="text-gray-800 font-semibold">QAR {sale.clientSubscription.subscription_cost}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Start Date:</span>
                  <span className="text-gray-800 font-semibold">{formatDate(sale.clientSubscription.start_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">End Date:</span>
                  <span className="text-gray-800 font-semibold">{formatDate(sale.clientSubscription.end_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Created Date:</span>
                  <span className="text-gray-800 font-semibold">{formatDate(sale.clientSubscription.created_date)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Payment Status:</span>
                  <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-semibold">
                    {sale.clientSubscription.payment_status}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Services & Pricing Table */}
                <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
                <table className="w-full border-collapse">
                  <thead>
                  <tr className="bg-gradient-to-r from-gray-700 to-gray-900 text-white">
                    <th className="p-4 text-left font-semibold">Description</th>
                    <th className="p-4 text-right font-semibold">Price</th>
                    <th className="p-4 text-right font-semibold">Discount</th>
                    <th className="p-4 text-right font-semibold">Total</th>
                  </tr>
                  </thead>
                  <tbody className="bg-white">
                  <tr className="border-b border-gray-200">
                    <td className="p-4">
                    <div>
                      <p className="font-bold text-lg text-gray-800">{sale.action}</p>
                      <p className="text-sm text-gray-600 mt-1">
                      Payment Method: <span className="font-semibold">{sale.payment_type}</span>
                  
                      </p>

                      {/* Services List */}
                      {sale.services && (
                      <>
                        {JSON.parse(sale.services).washCosts?.selected?.length > 0 && (
                        <div className="mt-3 pl-2 border-l-2 border-blue-400">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Wash Services:</p>
                          {JSON.parse(sale.services).washCosts?.selected.map((wash, idx) => (
                          <div key={idx} className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">{wash.serviceType?.name_en || 'Service'}</span>
                            {wash.vehicleType && <span> - {wash.vehicleType.name_en}</span>}
                            <span className="float-right font-semibold">QAR {wash.cost}</span>
                          </div>
                          ))}
                        </div>
                        )}
                        
                        {JSON.parse(sale.services).services?.selected?.length > 0 && (
                        <div className="mt-3 pl-2 border-l-2 border-green-400">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Additional Services:</p>
                          {JSON.parse(sale.services).services.selected.map((service, idx) => (
                          <div key={idx} className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">{service.name_en || service.name}</span>
                            <span className="float-right font-semibold">QAR {service.cost}</span>
                          </div>
                          ))}
                        </div>
                        )}
                        
                        {JSON.parse(sale.services).products?.selected?.length > 0 && (
                        <div className="mt-3 pl-2 border-l-2 border-purple-400">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Products:</p>
                          {JSON.parse(sale.services).products.selected.map((product, idx) => (
                          <div key={idx} className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">{product.name_en || product.name}</span>
                            <span className="float-right font-semibold">QAR {product.cost}</span>
                          </div>
                          ))}
                        </div>
                        )}
                        
                        {JSON.parse(sale.services).addons?.selected?.length > 0 && (
                        <div className="mt-3 pl-2 border-l-2 border-orange-400">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Add-ons:</p>
                          {JSON.parse(sale.services).addons.selected.map((addon, idx) => (
                          <div key={idx} className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">{addon.name_en || addon.name}</span>
                            <span className="float-right font-semibold">QAR {addon.cost}</span>
                          </div>
                          ))}
                        </div>
                        )}
                      </>
                      )}
                      
                      {sale.coupon_id && (
                      <p className="text-sm text-green-600 mt-3">
                        Coupon Applied: #{sale.coupon_id}
                      </p>
                      )}
                    </div>
                    </td>
                    <td className="p-4 text-right">
                    <span className="text-lg font-semibold text-gray-800">QAR {sale.price.toFixed(2)}</span>
                    </td>
                    <td className="p-4 text-right">
                    <span className="text-lg font-semibold text-red-600">
                      {sale.discount > 0 ? `-QAR ${sale.discount.toFixed(2)}` : 'QAR 0.00'}
                    </span>
                    </td>
                    <td className="p-4 text-right">
                    <span className="text-xl font-bold text-green-600">QAR {sale.total.toFixed(2)}</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
                </div>

                {/* Totals Summary */}
          <div className="flex justify-end mb-6">
            <div className="w-80 bg-gray-50 p-6 rounded-xl border border-gray-200">
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-300">
                  <span className="font-semibold text-gray-700">Subtotal:</span>
                  <span className="font-semibold text-gray-800">QAR {sale.price.toFixed(2)}</span>
                </div>
                {sale.discount > 0 && (
                  <div className="flex justify-between py-2 border-b border-gray-300">
                    <span className="font-semibold text-gray-700">Discount:</span>
                    <span className="font-semibold text-red-600">- QAR {sale.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between py-3 border-t-2 border-gray-800">
                  <span className="text-xl font-bold text-gray-800">Total Amount:</span>
                  <span className="text-2xl font-bold text-blue-600">QAR {sale.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Badge */}
          <div className="flex items-center justify-center gap-3 mb-8 p-5 bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg">
            <CreditCard className="text-white" size={32} />
            <span className="text-xl font-bold text-white">
              PAYMENT CONFIRMED - {sale.payment_type}
            </span>
          </div>

          {/* Terms and Conditions */}
          {/* <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-2">Terms & Conditions:</h4>
            <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
              <li>This invoice is valid for the services mentioned above</li>
              <li>Subscription validity is subject to the dates mentioned</li>
              <li>For any queries, please contact us within 7 days</li>
              <li>Refunds are subject to company policy</li>
            </ul>
          </div> */}

          {/* Footer */}
          <div className="border-t-4 border-blue-600 pt-6 mt-8">
            <div className="text-center mb-4">
              <h2 className="font-bold text-gray-800 text-2xl mb-3">GO CLEANS</h2>
              <p className="text-gray-700 font-semibold mb-2">Premium Car Wash Services - Qatar</p>
              <div className="space-y-1 text-gray-600">
                <p className="flex items-center justify-center gap-2">
                  <MapPin size={16} />
                  <span>Commercial Avenue, Umm Al Seneem, Doha, Qatar</span>
                </p>
                <p>📞 Mobile: +974 7020 5550</p>
                <p>📧 Email: info@gocleans.co</p>
                <p>🌐 Website: www.gocleans.co</p>
              </div>
            </div>
            <div className="text-center pt-4 border-t border-gray-300">
              <p className="text-sm italic text-gray-600 font-medium">
                Thank you for choosing GO CLEANS! We appreciate your business and look forward to serving you again.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesInvoice;


//show admin, and add admin to filters